import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Target, Eye, Globe, Award, CheckCircle, Users, TrendingUp, Shield, Briefcase, Menu, X, Mail, MapPin, Twitter, Linkedin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const About = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { number: '150+', label: 'Custom Solutions Delivered', icon: CheckCircle },
    { number: '20+', label: 'Years Experience', icon: Award },
    { number: '50+', label: 'Happy Clients', icon: Users },
    { number: '24/7', label: 'Support Available', icon: Shield }
  ];

  const offices = [
    {
      location: 'Ghana Office',
      address: 'Block 8, Science Museum, Banson Road',
      city: 'Near Workers College, Accra, Ghana',
      postal: 'P.O. Box 16110 K.I.A, Accra',
      phone: 'Tel: 231694 Adabraka, Accra'
    },
    {
      location: 'USA Office',
      address: '10432 Balls Ford Rd Suite 300',
      city: 'Manassas, VA 20109',
      postal: 'United States of America',
      phone: 'Phone: +1 (571) 406-4203'
    }
  ];

  const values = [
    { icon: Shield, title: 'Reliability', description: 'Consistent, dependable service delivery' },
    { icon: TrendingUp, title: 'Innovation', description: 'Cutting-edge technology solutions' },
    { icon: Users, title: 'Partnership', description: 'Building lasting relationships' },
    { icon: Award, title: 'Excellence', description: 'Premium quality in every project' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                alt="HNS Logo"
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#18246F]">HNS</h1>
                <p className="text-xs text-gray-600">...technology with infinite solutions</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Home</Link>
              <Link to="/about" className="text-[#18246F] font-semibold">About</Link>
              <Link to="/services" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Services</Link>
              <Link to="/projects" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Projects</Link>
              <Link to="/team" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Team</Link>
              <Link to="/contact" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Contact</Link>
              <Button onClick={() => navigate('/contact')} className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white">Let's Connect</Button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-[#18246F] bg-gray-100 rounded-lg font-semibold">About</Link>
              <Link to="/services" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Services</Link>
              <Link to="/projects" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Projects</Link>
              <Link to="/team" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Team</Link>
              <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Contact</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#18246F] via-[#293074] to-[#3d4a8f] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">About Us</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Hyperlink Network Systems</h1>
          <p className="text-xl text-white/90 max-w-3xl">Leading the ICT industry since 2004 with incomparably high quality and upscale services</p>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-[#8B2635] to-[#6d1d29] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="mx-auto mb-4" size={48} />
                  <p className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</p>
                  <p className="text-sm md:text-base text-white/90">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl font-bold text-[#18246F] mb-6">Company Overview</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Hyperlink Network Systems (HNS) is an information systems company specializing in network, fiber optic installation, and telecommunication management. Established in 2004, we have been transforming ideas into successful ventures with innovative technology solutions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our journey over the past decade has been focused on delivering solutions and building partnerships. This has enabled us to create an environment where our customers have become part of our family.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#3C74FF]" size={24} />
                  <span className="text-gray-700">Company Code: CA-9904</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#3C74FF]" size={24} />
                  <span className="text-gray-700">VAT/TIN: 324C002393</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#3C74FF]" size={24} />
                  <span className="text-gray-700">MPN: 5233007 (USA) | 6254081 (Ghana)</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/mtjez97o_WhatsApp%20Image%202025-11-08%20at%2002.29.26_82f8df31.jpg"
                alt="HNS Team"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-[#3C74FF]/20 hover:border-[#3C74FF] transition-all">
              <CardHeader>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gradient-to-br from-[#3C74FF] to-[#616EB7] p-4 rounded-xl">
                    <Target className="text-white" size={32} />
                  </div>
                  <CardTitle className="text-2xl">Mission Statement</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To lead the ICT industry in the reliable provision of information systems solutions and to be globally recognized for our incomparably high-quality and premium services.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#616EB7]/20 hover:border-[#616EB7] transition-all">
              <CardHeader>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gradient-to-br from-[#616EB7] to-[#3C74FF] p-4 rounded-xl">
                    <Eye className="text-white" size={32} />
                  </div>
                  <CardTitle className="text-2xl">Vision Statement</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  To help optimize our clients' productivity through the provision of real-time, world-class, reliable, cost-effective, and comprehensive information and communication technology solutions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Our Core <span className="text-[#3C74FF]">Values</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">The principles that guide every decision and action we take</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all border-2 border-gray-100 hover:border-[#3C74FF]">
                  <CardContent className="pt-8">
                    <div className="bg-[#3C74FF]/10 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-[#3C74FF]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-[#18246F] mb-2">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[#3C74FF]/10 text-[#3C74FF] border-[#3C74FF]/20">Global Presence</Badge>
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Our <span className="text-[#3C74FF]">Locations</span></h2>
            <p className="text-gray-600">Serving clients across multiple continents</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {offices.map((office, index) => (
              <Card key={index} className="hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <Globe className="text-[#3C74FF]" size={28} />
                    <CardTitle className="text-2xl">{office.location}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-gray-700">{office.address}</p>
                  <p className="text-gray-700">{office.city}</p>
                  <p className="text-gray-700">{office.postal}</p>
                  <p className="text-gray-700 font-semibold">{office.phone}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Microsoft Partner */}
      <section className="py-20 bg-gradient-to-br from-[#18246F] to-[#3C74FF] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold mb-4">Microsoft Certified Partner</h2>
          <p className="text-xl text-white/90 mb-6">MPN 5233007 (USA) | MPN 6254081 (Ghana)</p>
          <p className="text-xl text-white/90">Zoho Partner ID - PU5475643</p>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#18246F] mb-4">Ready to Work Together?</h2>
          <p className="text-gray-600 mb-8">Let's discuss how we can help transform your IT infrastructure</p>
          <Button 
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white"
          >
            Contact Us Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18246F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                  alt="HNS Logo"
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">HNS</h3>
                  <p className="text-xs text-gray-300">...technology with infinite solutions</p>
                </div>
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Leading the ICT industry with reliable information systems solutions with over 20 years of excellence in technology delivery.
              </p>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Award size={16} />
                <span>Trusted by 50+ Organizations</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-white transition-colors">Projects</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Network Security</li>
                <li>Cloud Management</li>
                <li>Software Development</li>
                <li>VoIP Solutions</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>info@hnsgh.com</span>
                </li>
                <li className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>Accra, Ghana | Manassas, VA USA</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hyperlink Network Systems Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a 
        href="tel:+15714064203"
        className="fixed bottom-8 left-8 bg-[#3C74FF] hover:bg-[#2d5fd9] text-white rounded-full p-4 shadow-2xl z-50 flex items-center gap-3 group transition-all duration-300 hover:scale-110"
      >
        <Phone size={24} className="animate-pulse" />
        <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap">
          +1 (571) 406-4203
        </span>
      </a>
    </div>
  );
};

export default About;
